import React, { useState } from "react";
import T from '../../components/common/T';
import { useWorkforce } from "../../context/WorkforceContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal"; 
import { useAuth } from "../../context/AuthContext";
import DataTable from "../../components/common/DataTables.jsx";
import PageHeader from "../../components/common/PageHeader";
import TableActionButtons from "../../components/common/TableActionButtons";

const EmployeeListView = ({ onEdit, filter = "All", search = "" }) => {
  const { workforce = [], deleteWorkforce } = useWorkforce();
  const navigate = useNavigate();
  const { checkAccess } = useAuth();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  const filtered = workforce.filter((emp) => {
    const status = emp.status || "Active";
    const matchesFilter = filter === "All" || status === filter;
    const q = (search || "").toLowerCase();
    const matchesSearch =
      (emp.name || "").toLowerCase().includes(q) ||
      (emp.role || "").toLowerCase().includes(q) ||
      (emp.employee_id || "").toLowerCase().includes(q);
    return matchesFilter && matchesSearch;
  });

  const handleDeleteClick = (employee) => {
    setEmployeeToDelete(employee);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (employeeToDelete) {
        deleteWorkforce(employeeToDelete.id);
        setIsModalOpen(false);
        setEmployeeToDelete(null);
    }
  };

  const columns = [
    {
      header: "Employee",
      accessor: "name",
      sortable: true,
      render: (employee) => (
        <div>
          <div className="font-semibold text-gray-900 dark:text-white">{employee.name}</div>
          <div className="text-[10px] text-gray-500 dark:text-slate-400 font-mono">{employee.employee_id}</div>
        </div>
      )
    },
    {
      header: "Role / Dept",
      accessor: "role",
      sortable: true,
      render: (employee) => (
        <div>
          <div className="text-[11px] text-gray-900 dark:text-white font-semibold">{employee.role}</div>
          <div className="text-[10px] text-gray-500 dark:text-slate-400">{employee.department || "General"}</div>
        </div>
      )
    },
    {
      header: "Contact",
      accessor: "email",
      render: (employee) => (
        <div>
          <div className="text-[11px] text-gray-700 dark:text-slate-300">{employee.email}</div>
          <div className="text-[10px] text-gray-500 dark:text-slate-400">{employee.phone}</div>
        </div>
      )
    },
    {
      header: "Status",
      accessor: "status",
      align: "center",
      sortable: true,
      render: (employee) => (
        <span className={`px-2 py-0.5 text-[10px] rounded border font-semibold ${
          employee.status === "Active" ? "bg-green-50 text-green-700 border-green-200" : 
          employee.status === "On Leave" ? "bg-yellow-50 text-yellow-700 border-yellow-200" : 
          "bg-gray-50 text-gray-600 border-gray-200"
        }`}>
          {employee.status || "Active"}
        </span>
      )
    },
    {
      header: "Actions",
      align: "right",
      render: (employee) => (
        <TableActionButtons
          onView={() => navigate(`/admin/employee/view/${employee.id}`)}
          onEdit={checkAccess('workforce.edit') ? () => onEdit(employee) : undefined}
          onDelete={checkAccess('workforce.delete') ? () => handleDeleteClick(employee) : undefined}
        />
      )
    }
  ];

  return (
    <>
      <ConfirmationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
        title="Remove Employee?"
        message={`Are you sure you want to remove ${employeeToDelete?.name}? System access will be revoked.`}
      />
      <div className="relative">
        <DataTable data={filtered} columns={columns} emptyMessage="No employees found." />
      </div>
    </>
  );
};

export default EmployeeListView;